<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { fromUnixTime, differenceInCalendarDays } from 'date-fns';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const contactName = computed(
  () => props.conversation.contact?.name || props.conversation.contact?.id
);

const assignee = computed(() => props.conversation.meta?.assignee);

const formattedValue = computed(() => {
  if (!props.conversation.value) return null;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(props.conversation.value);
});

const daysInStage = computed(() => {
  if (!props.conversation.kanban_stage_changed_at) return 0;
  return differenceInCalendarDays(
    new Date(),
    fromUnixTime(props.conversation.kanban_stage_changed_at)
  );
});

const changedAtLabel = computed(() => {
  if (!props.conversation.kanban_stage_changed_at) return '';
  return dynamicTime(props.conversation.kanban_stage_changed_at);
});
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 bg-n-solid-2 border border-n-weak rounded-lg cursor-grab active:cursor-grabbing hover:border-n-weak-hover"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ contactName }}
      </span>
      <span
        class="shrink-0 py-0.5 px-1.5 text-xs rounded-md capitalize bg-n-slate-3 text-n-slate-11"
      >
        {{ conversation.status }}
      </span>
    </div>

    <div class="flex items-center justify-between gap-2">
      <span class="text-xs text-n-slate-11">
        {{
          changedAtLabel ||
          t('KANBAN.BOARD.CARD.DAYS_IN_STAGE', { n: daysInStage })
        }}
      </span>
      <Avatar
        v-if="assignee"
        :name="assignee.name"
        :src="assignee.thumbnail"
        :size="20"
      />
    </div>

    <div
      v-if="formattedValue"
      class="self-start py-0.5 px-1.5 text-xs font-medium rounded-md bg-n-teal-3 text-n-teal-11"
    >
      {{ formattedValue }}
    </div>
  </div>
</template>
